// src/pages/SuperAdmin/BugReports.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Bug, Filter, Search, AlertCircle, CheckCircle2, Clock, XCircle, MessageSquare, Loader } from 'lucide-react';
import BugReportService from '../../services/BugReportService';

const SuperAdminBugReports = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [selectedBug, setSelectedBug] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadBugReports();
  }, []);

  const loadBugReports = async () => {
    try {
      setLoading(true);
      const reports = await BugReportService.getAllBugReports();
      setBugs(reports);
    } catch (error) {
      console.error('Error loading bug reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bugId, newStatus) => {
    try {
      setUpdating(true);
      await BugReportService.updateBugStatus(bugId, newStatus, notes);
      
      // Update local state
      setBugs(prevBugs =>
        prevBugs.map(bug =>
          bug.id === bugId
            ? { ...bug, status: newStatus, notes, updatedAt: new Date().toISOString() }
            : bug
        )
      );

      if (showDetailModal) {
        setShowDetailModal(false);
        setSelectedBug(null);
        setNotes('');
      }

      alert('✅ Bug status updated successfully!');
    } catch (error) {
      console.error('Error updating bug status:', error);
      alert('Failed to update bug status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const openDetailModal = (bug) => {
    setSelectedBug(bug);
    setNotes(bug.notes || '');
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBug(null);
    setNotes('');
  };

  const filteredBugs = useMemo(() => {
    return bugs.filter(bug => {
      const matchesSearch =
        bug.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bug.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bug.restaurantName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'All' || bug.status === filterStatus;
      const matchesPriority = filterPriority === 'All' || bug.priority === filterPriority;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [bugs, searchTerm, filterStatus, filterPriority]);

  const stats = useMemo(() => {
    return {
      total: bugs.length,
      open: bugs.filter(b => b.status === 'Open').length,
      inProgress: bugs.filter(b => b.status === 'In Progress').length,
      resolved: bugs.filter(b => b.status === 'Resolved').length,
      high: bugs.filter(b => b.priority === 'High' && b.status !== 'Resolved').length
    };
  }, [bugs]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'Closed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open': return <AlertCircle className="w-5 h-5" />;
      case 'In Progress': return <Clock className="w-5 h-5" />;
      case 'Resolved': return <CheckCircle2 className="w-5 h-5" />;
      case 'Closed': return <XCircle className="w-5 h-5" />;
      default: return <Bug className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading bug reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Bug Reports</h1>
        <p className="text-gray-600">Manage and track bug reports from restaurant owners</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-gray-500">
          <p className="text-gray-600 text-xs md:text-sm mb-1">Total Reports</p>
          <p className="text-2xl md:text-3xl font-bold text-gray-800">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-gray-600 text-xs md:text-sm mb-1">Open</p>
          <p className="text-2xl md:text-3xl font-bold text-red-600">{stats.open}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <p className="text-gray-600 text-xs md:text-sm mb-1">In Progress</p>
          <p className="text-2xl md:text-3xl font-bold text-blue-600">{stats.inProgress}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <p className="text-gray-600 text-xs md:text-sm mb-1">Resolved</p>
          <p className="text-2xl md:text-3xl font-bold text-green-600">{stats.resolved}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <p className="text-gray-600 text-xs md:text-sm mb-1">High Priority</p>
          <p className="text-2xl md:text-3xl font-bold text-orange-600">{stats.high}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search bug reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 border border-gray-300 px-3 py-2 rounded-lg">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="outline-none text-sm bg-transparent"
            >
              <option value="All">All Status</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div className="flex items-center gap-2 border border-gray-300 px-3 py-2 rounded-lg">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="outline-none text-sm bg-transparent"
            >
              <option value="All">All Priority</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={loadBugReports}
            disabled={loading}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition whitespace-nowrap"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Bug Reports List */}
      {filteredBugs.length > 0 ? (
        <div className="space-y-4">
          {filteredBugs.map(bug => (
            <div
              key={bug.id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden"
            >
              <div className="p-4 md:p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${getStatusColor(bug.status).split(' ')[0]}`}>
                        {getStatusIcon(bug.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-800 mb-1 break-words">{bug.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium">{bug.restaurantName}</span>
                          <span>•</span>
                          <span>{new Date(bug.createdAt).toLocaleString()}</span>
                          <span>•</span>
                          <span className="text-xs text-gray-500">{bug.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(bug.priority)}`}>
                      {bug.priority}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(bug.status)}`}>
                      {bug.status}
                    </span>
                  </div>
                </div>

                {/* Description Preview */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{bug.description}</p>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openDetailModal(bug)}
                    className="flex-1 md:flex-none px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
                  >
                    View Details
                  </button>
                  {bug.status === 'Open' && (
                    <button
                      onClick={() => handleStatusUpdate(bug.id, 'In Progress')}
                      disabled={updating}
                      className="flex-1 md:flex-none px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition text-sm font-medium"
                    >
                      Start Working
                    </button>
                  )}
                  {bug.status === 'In Progress' && (
                    <button
                      onClick={() => handleStatusUpdate(bug.id, 'Resolved')}
                      disabled={updating}
                      className="flex-1 md:flex-none px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition text-sm font-medium"
                    >
                      Mark Resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Bug className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Bug Reports Found</h3>
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'All' || filterPriority !== 'All'
              ? 'Try adjusting your filters'
              : 'Bug reports from restaurant owners will appear here'}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedBug && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getStatusColor(selectedBug.status).split(' ')[0]}`}>
                  {getStatusIcon(selectedBug.status)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Bug Report Details</h2>
                  <p className="text-sm text-gray-600">ID: {selectedBug.id}</p>
                </div>
              </div>
              <button
                onClick={closeDetailModal}
                className="text-gray-500 hover:text-gray-700 p-1"
                disabled={updating}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Bug Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Restaurant</p>
                  <p className="font-semibold text-gray-800">{selectedBug.restaurantName}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Category</p>
                  <p className="font-semibold text-gray-800">{selectedBug.category}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Priority</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedBug.priority)}`}>
                    {selectedBug.priority}
                  </span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedBug.status)}`}>
                    {selectedBug.status}
                  </span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Reported</p>
                  <p className="font-semibold text-gray-800 text-sm">{new Date(selectedBug.createdAt).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Last Updated</p>
                  <p className="font-semibold text-gray-800 text-sm">{new Date(selectedBug.updatedAt).toLocaleString()}</p>
                </div>
              </div>

              {/* Title */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Bug Title</h3>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedBug.title}</p>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Description</h3>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selectedBug.description}</p>
              </div>

              {/* Admin Notes */}
              <div>
                <label className=".block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Admin Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="4"
                  placeholder="Add internal notes about this bug report..."
                  disabled={updating}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                {selectedBug.status === 'Open' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'In Progress')}
                      disabled={updating}
                      className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      {updating ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5" />
                          Mark In Progress
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'Resolved')}
                      disabled={updating}
                      className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Mark Resolved
                    </button>
                  </>
                )}
                {selectedBug.status === 'In Progress' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'Resolved')}
                      disabled={updating}
                      className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      {updating ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          Mark Resolved
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'Open')}
                      disabled={updating}
                      className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      Reopen
                    </button>
                  </>
                )}
                {selectedBug.status === 'Resolved' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'Closed')}
                      disabled={updating}
                      className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      {updating ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          Close Bug
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedBug.id, 'In Progress')}
                      disabled={updating}
                      className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition font-medium"
                    >
                      Reopen as In Progress
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminBugReports;